import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import CheckoutDialog from "@/components/CheckoutDialog";
import AuthDialog from "@/components/AuthDialog";
import { toast } from "sonner";

export default function CartSheet({ open, onOpenChange }) {
  const { cart, updateCartQty, removeFromCart, cartTotal, user } = useApp();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const startCheckout = () => {
    if (!user) {
      toast.info("Please login to place an order");
      setAuthOpen(true);
      return;
    }
    onOpenChange(false);
    setTimeout(() => setCheckoutOpen(true), 200);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-sheet">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <SheetTitle className="font-display text-xl">Your Cart ({cart.length})</SheetTitle>
          </SheetHeader>
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <ShoppingBag className="w-7 h-7 text-emerald-700" />
              </div>
              <div className="font-display text-lg font-semibold">Cart is khaali</div>
              <p className="text-sm text-slate-500 max-w-xs">Explore nearby shops and add products to your cart.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-3 border border-slate-100 rounded-2xl p-3" data-testid={`cart-item-${item.id}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{item.name}</div>
                    <div className="text-xs text-slate-500 truncate">{item.shop_name}</div>
                    <div className="text-xs text-slate-400">{item.unit}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 border border-slate-200 rounded-full">
                        <button onClick={() => updateCartQty(item.id, item.qty - 1)} className="p-1.5 hover:bg-slate-50 rounded-full" data-testid={`decrease-${item.id}`}><Minus className="w-3 h-3" /></button>
                        <span className="text-sm font-semibold min-w-[20px] text-center">{item.qty}</span>
                        <button onClick={() => updateCartQty(item.id, item.qty + 1)} className="p-1.5 hover:bg-slate-50 rounded-full" data-testid={`increase-${item.id}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="text-sm font-bold text-emerald-800">₹{item.price * item.qty}</div>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="text-slate-400 hover:text-red-500 self-start p-1" aria-label="Remove" data-testid={`remove-${item.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {cart.length > 0 && (
            <div className="border-t border-slate-100 px-6 py-4 space-y-3 bg-slate-50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">₹{cartTotal}</span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="font-semibold">Total (before delivery)</span>
                <span className="font-display text-xl font-bold text-emerald-800" data-testid="cart-total">₹{cartTotal}</span>
              </div>
              <button onClick={startCheckout} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl py-3 font-semibold text-sm transition-colors" data-testid="checkout-btn">
                Proceed to Checkout
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
